import React, { Suspense, lazy, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { supabase } from '../supabase';
import { useWhitelistRole } from '../useWhitelistRole';
import RichText, { truncateText } from './RichText';
import { sheet, toastMobile, tap } from '../mobileStyles';
import useBreakpoint from '../hooks/useBreakpoint';

const FILES_BUCKET = 'manuales-archivos';
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const CONTENIDO_PREVIEW = 200;
const MAX_THUMBS = 4;

const ACCEPTED_MIME = {
  'image/jpeg': 'imagen',
  'image/png': 'imagen',
  'image/webp': 'imagen',
  'image/gif': 'imagen',
  'application/pdf': 'pdf',
};
const ACCEPTED_EXT = { jpg: 'imagen', jpeg: 'imagen', png: 'imagen', webp: 'imagen', gif: 'imagen', pdf: 'pdf' };
const ACCEPT_ATTR = '.jpg,.jpeg,.png,.webp,.gif,.pdf,image/jpeg,image/png,image/webp,image/gif,application/pdf';

// 'imagen' | 'pdf' | null (unsupported). Falls back to the extension when the
// browser reports an empty MIME type.
function fileKind(file) {
  if (ACCEPTED_MIME[file.type]) return ACCEPTED_MIME[file.type];
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return ACCEPTED_EXT[ext] || null;
}

function fmtSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Storage keys reject accents/spaces/etc. Keep the extension readable.
function safeFileName(name) {
  return name
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(-120) || 'archivo';
}

function archivosOf(entry) {
  return Array.isArray(entry?.archivos) ? entry.archivos : [];
}

// Public URL format: https://<proj>.supabase.co/storage/v1/object/public/manuales-archivos/<path>
function storagePathFromUrl(url) {
  const marker = `/${FILES_BUCKET}/`;
  const idx = (url || '').indexOf(marker);
  if (idx < 0) return null;
  return decodeURIComponent(url.slice(idx + marker.length).split('?')[0]);
}

async function removeFromStorage(archivos) {
  const paths = archivos.map(a => storagePathFromUrl(a.url)).filter(Boolean);
  if (paths.length === 0) return;
  const { error } = await supabase.storage.from(FILES_BUCKET).remove(paths);
  // eslint-disable-next-line no-console
  if (error) console.warn('[manuales] storage.remove failed:', error);
}

// Supabase-js upload() has no progress callback, so hit the Storage REST
// endpoint with XHR to get upload.onprogress.
function uploadWithProgress(path, file, onProgress) {
  const baseUrl = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const encodedPath = path.split('/').map(encodeURIComponent).join('/');
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseUrl}/storage/v1/object/${FILES_BUCKET}/${encodedPath}`);
    xhr.setRequestHeader('Authorization', `Bearer ${key}`);
    xhr.setRequestHeader('apikey', key);
    xhr.setRequestHeader('x-upsert', 'false');
    xhr.setRequestHeader('cache-control', 'max-age=3600');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) onProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const { data } = supabase.storage.from(FILES_BUCKET).getPublicUrl(path);
        resolve(data.publicUrl);
      } else {
        let msg = `HTTP ${xhr.status}`;
        try { msg = JSON.parse(xhr.responseText).message || msg; } catch (_) { /* keep status */ }
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error('Error de red'));
    xhr.send(file);
  });
}

// Annotations are keyed by archivo_url, so they go when their file goes.
async function removeAnnotationsFor(manualId, archivos) {
  const urls = archivos.map(a => a.url).filter(Boolean);
  if (!manualId || urls.length === 0) return;
  const { error } = await supabase.from('manual_anotaciones').delete().eq('manual_id', manualId).in('archivo_url', urls);
  // eslint-disable-next-line no-console
  if (error) console.warn('[manuales] annotation cleanup failed:', error);
}

// archivo_url -> annotation count for the given manuales. Paged because
// PostgREST caps each response at 1000 rows.
async function fetchAnnotationCounts(manualIds) {
  const counts = {};
  if (!manualIds.length) return counts;
  const PAGE = 1000;
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('manual_anotaciones')
      .select('archivo_url')
      .in('manual_id', manualIds)
      .range(from, from + PAGE - 1);
    if (error || !data) break;
    data.forEach(r => { counts[r.archivo_url] = (counts[r.archivo_url] || 0) + 1; });
    if (data.length < PAGE) break;
  }
  return counts;
}

// pdf.js is heavy — only load the viewer when an attachment is opened.
const AttachmentViewer = lazy(() => import('./AttachmentViewer'));

// Supabase serves Content-Disposition: attachment when ?download= is present,
// which works cross-origin (the <a download> attribute doesn't).
function downloadUrl(archivo) {
  return `${archivo.url}${archivo.url.includes('?') ? '&' : '?'}download=${encodeURIComponent(archivo.nombre || '')}`;
}

let fileKeySeq = 0;
function existingItem(archivo) {
  fileKeySeq += 1;
  return { key: `e${fileKeySeq}`, kind: 'existing', archivo, nombre: archivo.nombre, tipo: archivo.tipo, tamano: archivo.tamano };
}

const TAB_DEFS = [
  { value: 'diseño', label: '📐 Manual de Diseño' },
  { value: 'obra',   label: '🏗️ Manual de Obra' },
];

const ENTRY_TYPES = [
  {
    value: 'error', label: 'Error', plural: 'Errores', icon: '⚠️',
    color: '#B83B3B', bg: '#FDEEEE', border: '#F0C4C4',
    emptyText: 'No hay errores registrados',
  },
  {
    value: 'recomendacion', label: 'Recomendación', plural: 'Recomendaciones', icon: '✅',
    color: '#2E6B45', bg: '#EEF6F0', border: '#C4E0CE',
    emptyText: 'No hay recomendaciones registradas',
  },
  {
    value: 'advertencia', label: 'Advertencia', plural: 'Advertencias', icon: '🔶',
    color: '#96610F', bg: '#FFF6E8', border: '#F0DCBC',
    emptyText: 'No hay advertencias registradas',
  },
];

function fmtEntryDate(iso) {
  if (!iso) return '';
  return new Date(iso)
    .toLocaleDateString('es-PE', { day: 'numeric', month: 'short', year: 'numeric' })
    .replace(/\./g, '');
}

const EMPTY_FORM = {
  tipo: 'diseño',
  tipo_entrada: 'error',
  titulo: '',
  descripcion: '',
  contenido: '',
  proyecto_nombre: '',
  categoria: '',
};

export default function Manuales() {
  const { user } = useUser();
  const { isAdmin, nombre: whitelistNombre } = useWhitelistRole();
  const [activeTipo, setActiveTipo] = useState('diseño');
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEntryType, setFilterEntryType] = useState('all');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [team, setTeam] = useState([]);
  const [onlyWithFiles, setOnlyWithFiles] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [viewer, setViewer] = useState(null); // { entryId, index }
  const [annCounts, setAnnCounts] = useState({}); // archivo_url -> count
  // Files shown in the modal: kind 'existing' (already in archivos) or 'new'
  // (a local File not uploaded yet). Nothing touches Storage until save.
  const [modalFiles, setModalFiles] = useState([]);
  const [removedArchivos, setRemovedArchivos] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const contenidoRef = useRef(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const { isMobile, isCompact, isTouch } = useBreakpoint();
  const isDesktop = !isCompact;
  const canHover = !isTouch;

  const myEmail = (user?.primaryEmailAddress?.emailAddress || '').toLowerCase();
  // Same resolution as Journal: equipo row by Clerk email, then whitelist
  // nombre, then the Clerk profile.
  const myAutor = useMemo(() => {
    const member = myEmail ? team.find(m => (m.email || '').toLowerCase() === myEmail) : null;
    return (
      member?.nombre ||
      whitelistNombre ||
      user?.fullName ||
      [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
      myEmail ||
      ''
    );
  }, [team, myEmail, whitelistNombre, user]);

  // Legacy entries have no autor_email → admin-only.
  const canEditEntry = (entry) => !!entry && (
    isAdmin || (!!myEmail && !!entry.autor_email && entry.autor_email.toLowerCase() === myEmail)
  );
  const canDeleteFile = (archivo) => !!archivo && (
    isAdmin || (!!myEmail && (archivo.subido_por || '').toLowerCase() === myEmail)
  );

  function showToast(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [eRes, pRes, cRes, tRes] = await Promise.all([
      supabase.from('manuales').select('*').eq('activo', true).order('created_at', { ascending: false }),
      supabase.from('proyectos').select('id, nombre').order('nombre'),
      supabase.from('categorias').select('id, nombre, tipo').order('nombre'),
      supabase.from('equipo').select('id, nombre, email, color'),
    ]);
    if (eRes.data) {
      setEntries(eRes.data);
      const withFiles = eRes.data.filter(e => archivosOf(e).length > 0).map(e => e.id);
      fetchAnnotationCounts(withFiles).then(setAnnCounts);
    }
    if (pRes.data) setProjects(pRes.data);
    if (cRes.data) setCategorias(cRes.data);
    if (tRes.data) setTeam(tRes.data);
    setLoading(false);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Categorías filtered by the current tab tipo (diseño/obra). Mirrors what
  // the Etiquetas / Gantt views do — historical categories not in the table
  // are still allowed via the dropdown's free-text fallback.
  const categoriasForTipo = useMemo(
    () => categorias.filter(c => (c.tipo || 'obra') === activeTipo),
    [categorias, activeTipo],
  );

  // Everything except the tipo_entrada tab — the tab counts come from this.
  const filteredByOthers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter(e => {
      if (e.tipo !== activeTipo) return false;
      if (filterCategoria !== 'all' && e.categoria !== filterCategoria) return false;
      if (onlyWithFiles && archivosOf(e).length === 0) return false;
      if (q) {
        const hay = `${e.titulo || ''} ${e.descripcion || ''} ${e.contenido || ''} ${e.proyecto_nombre || ''} ${e.categoria || ''} ${e.recomendacion_ia || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [entries, activeTipo, filterCategoria, onlyWithFiles, search]);

  const typeCounts = useMemo(() => {
    const counts = { all: filteredByOthers.length };
    ENTRY_TYPES.forEach(t => { counts[t.value] = 0; });
    filteredByOthers.forEach(e => { if (counts[e.tipo_entrada] !== undefined) counts[e.tipo_entrada] += 1; });
    return counts;
  }, [filteredByOthers]);

  const visibleEntries = useMemo(
    () => (filterEntryType === 'all' ? filteredByOthers : filteredByOthers.filter(e => e.tipo_entrada === filterEntryType)),
    [filteredByOthers, filterEntryType],
  );

  const hasActiveFilters = !!search.trim() || filterCategoria !== 'all' || onlyWithFiles;
  const sheetFilterCount = (filterCategoria !== 'all' ? 1 : 0) + (onlyWithFiles ? 1 : 0);

  function clearFilters() {
    setSearch('');
    setFilterCategoria('all');
    setOnlyWithFiles(false);
  }

  const categoriaOptionsForFilter = useMemo(() => {
    const set = new Set();
    for (const e of entries) {
      if (e.tipo !== activeTipo) continue;
      if (e.categoria) set.add(e.categoria);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [entries, activeTipo]);

  function resetModalFiles(items = []) {
    setModalFiles(prev => {
      prev.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
      return items;
    });
    setRemovedArchivos([]);
    setFileErrors([]);
    setDragOver(false);
  }

  function openCreate() {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      tipo: activeTipo,
      tipo_entrada: filterEntryType !== 'all' ? filterEntryType : EMPTY_FORM.tipo_entrada,
    });
    resetModalFiles();
    setModalOpen(true);
  }
  function openEdit(entry) {
    if (!canEditEntry(entry)) return;
    setEditing(entry);
    resetModalFiles(archivosOf(entry).map(existingItem));
    setForm({
      tipo: entry.tipo || 'diseño',
      tipo_entrada: entry.tipo_entrada || 'error',
      titulo: entry.titulo || '',
      descripcion: entry.descripcion || '',
      contenido: entry.contenido || '',
      proyecto_nombre: entry.proyecto_nombre || '',
      categoria: entry.categoria || '',
    });
    setModalOpen(true);
  }
  function closeModal() {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    resetModalFiles();
  }

  function addFiles(fileList) {
    const errors = [];
    const items = [];
    Array.from(fileList || []).forEach(file => {
      const tipo = fileKind(file);
      if (!tipo) {
        errors.push(`"${file.name}": formato no permitido (solo JPG, PNG, WEBP, GIF o PDF)`);
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        errors.push(`"${file.name}" pesa ${fmtSize(file.size)} — el máximo es 10 MB`);
        return;
      }
      fileKeySeq += 1;
      items.push({
        key: `n${fileKeySeq}`,
        kind: 'new',
        file,
        nombre: file.name,
        tipo,
        tamano: file.size,
        previewUrl: tipo === 'imagen' ? URL.createObjectURL(file) : null,
        progress: 0,
        status: 'idle', // idle | uploading | error
        error: null,
      });
    });
    setFileErrors(errors);
    if (items.length) setModalFiles(prev => [...prev, ...items]);
  }

  // Removing in the modal is staged — Storage is only touched after save.
  function removeModalFile(item) {
    if (item.kind === 'existing') {
      if (!canDeleteFile(item.archivo)) return;
      setRemovedArchivos(prev => [...prev, item.archivo]);
    } else if (item.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
    setModalFiles(prev => prev.filter(f => f.key !== item.key));
  }

  function patchModalFile(key, patch) {
    setModalFiles(prev => prev.map(f => (f.key === key ? { ...f, ...patch } : f)));
  }

  // Uploads pending files for a manual. Returns { uploaded: [{item, archivo}], failed: [item] }.
  async function uploadPending(manualId, pending) {
    const stamp = Date.now();
    const results = await Promise.all(pending.map(async (item, i) => {
      patchModalFile(item.key, { status: 'uploading', progress: 0, error: null });
      const path = `${manualId}/${stamp + i}-${safeFileName(item.nombre)}`;
      try {
        const url = await uploadWithProgress(path, item.file, pct => patchModalFile(item.key, { progress: pct }));
        patchModalFile(item.key, { progress: 100 });
        return {
          item,
          archivo: {
            nombre: item.nombre,
            url,
            tipo: item.tipo,
            tamano: item.tamano,
            subido_por: myEmail || null,
            subido_en: new Date().toISOString(),
          },
        };
      } catch (err) {
        patchModalFile(item.key, { status: 'error', error: err.message || 'Error al subir' });
        return { item, error: err };
      }
    }));
    return {
      uploaded: results.filter(r => r.archivo),
      failed: results.filter(r => r.error).map(r => r.item),
    };
  }

  // Quick-insert for the contenido textarea. List markers go at the start of
  // a line; bold wraps the selection (or a placeholder that gets selected).
  function insertFormat(kind) {
    const ta = contenidoRef.current;
    const value = form.contenido;
    const start = ta ? ta.selectionStart : value.length;
    const end = ta ? ta.selectionEnd : value.length;
    let insert;
    let selFrom;
    let selTo;
    if (kind === 'bold') {
      const selected = value.slice(start, end) || 'negrita';
      insert = `**${selected}**`;
      selFrom = start + 2;
      selTo = selFrom + selected.length;
    } else {
      const marker = kind === 'check' ? '☐ ' : '• ';
      const atLineStart = start === 0 || value[start - 1] === '\n';
      insert = `${atLineStart ? '' : '\n'}${marker}`;
      selFrom = start + insert.length;
      selTo = selFrom;
    }
    const next = value.slice(0, start) + insert + value.slice(end);
    setForm(p => ({ ...p, contenido: next }));
    requestAnimationFrame(() => {
      if (!contenidoRef.current) return;
      contenidoRef.current.focus();
      contenidoRef.current.setSelectionRange(selFrom, selTo);
    });
  }

  async function generateRecommendation(descripcion) {
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    if (!apiKey) {
      // No key configured — return null and let the entry be saved without AI text.
      // eslint-disable-next-line no-console
      console.warn('[manuales] REACT_APP_OPENAI_API_KEY missing — skipping AI recommendation');
      return null;
    }
    try {
      const res = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          temperature: 0.4,
          messages: [
            { role: 'system', content: 'Eres un asistente del estudio de arquitectura másquemuros. Convierte experiencias del equipo en recomendaciones prácticas y accionables.' },
            { role: 'user', content: `Convierte este error/experiencia en una recomendación práctica de máximo 2 oraciones para un equipo de arquitectura: ${descripcion}` },
          ],
        }),
      });
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn('[manuales] OpenAI error', res.status, await res.text());
        return null;
      }
      const data = await res.json();
      const text = data?.choices?.[0]?.message?.content?.trim();
      return text || null;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[manuales] AI recommendation failed', e);
      return null;
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (saving) return;
    if (!form.titulo.trim() || !form.descripcion.trim()) {
      showToast('Título y descripción son obligatorios', 'error');
      return;
    }
    setSaving(true);
    const recomendacion = editing && form.descripcion === editing.descripcion
      ? editing.recomendacion_ia
      : await generateRecommendation(form.descripcion.trim());
    const payload = {
      tipo: form.tipo,
      titulo: form.titulo.trim(),
      descripcion: form.descripcion.trim(),
      contenido: form.contenido.trim() ? form.contenido : null,
      proyecto_nombre: form.proyecto_nombre || null,
      categoria: form.categoria || null,
      tipo_entrada: form.tipo_entrada || 'error',
      recomendacion_ia: recomendacion,
      activo: true,
    };

    // New entries are inserted first so uploads can use the manual id in
    // their Storage path.
    let row = editing;
    if (!editing) {
      const resp = await supabase.from('manuales')
        .insert([{ ...payload, autor: myAutor || null, autor_email: myEmail || null, archivos: [] }])
        .select();
      if (resp.error || !resp.data?.[0]) {
        setSaving(false);
        showToast(resp.error ? 'Error: ' + resp.error.message : 'No se guardó (revisa políticas RLS de manuales)', 'error');
        return;
      }
      row = resp.data[0];
    }

    const pending = modalFiles.filter(f => f.kind === 'new');
    const { uploaded, failed } = pending.length
      ? await uploadPending(row.id, pending)
      : { uploaded: [], failed: [] };
    const kept = modalFiles.filter(f => f.kind === 'existing').map(f => f.archivo);
    const archivos = [...kept, ...uploaded.map(u => u.archivo)];

    const needsUpdate = !!editing || uploaded.length > 0;
    let saved = row;
    if (needsUpdate) {
      const fields = editing ? { ...payload, archivos } : { archivos };
      const resp = await supabase.from('manuales').update(fields).eq('id', row.id).select();
      if (resp.error || !resp.data?.[0]) {
        setSaving(false);
        // The DB doesn't reference the new uploads — clean them up.
        await removeFromStorage(uploaded.map(u => u.archivo));
        showToast(resp.error ? 'Error: ' + resp.error.message : 'No se guardó (revisa políticas RLS de manuales)', 'error');
        if (!editing) fetchAll();
        return;
      }
      saved = resp.data[0];
    }

    // Only now that the row no longer references them, delete staged removals.
    if (removedArchivos.length) {
      await removeFromStorage(removedArchivos);
      await removeAnnotationsFor(saved.id, removedArchivos);
    }

    setSaving(false);
    setEntries(prev => (prev.some(e => e.id === saved.id)
      ? prev.map(e => (e.id === saved.id ? saved : e))
      : [saved, ...prev]));

    if (failed.length) {
      // Keep the modal open on the saved entry so the failed files can be retried.
      uploaded.forEach(u => { if (u.item.previewUrl) URL.revokeObjectURL(u.item.previewUrl); });
      setEditing(saved);
      setModalFiles(prev => [
        ...archivosOf(saved).map(existingItem),
        ...prev.filter(f => failed.some(x => x.key === f.key)),
      ]);
      setRemovedArchivos([]);
      showToast(`No se pudieron subir ${failed.length} archivo(s). Reintenta o quítalos.`, 'error');
      return;
    }

    showToast(editing ? 'Entrada actualizada' : 'Entrada agregada');
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    resetModalFiles();
    fetchAll();
  }

  async function deleteArchivo(entry, archivo) {
    if (!canDeleteFile(archivo)) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`¿Eliminar el archivo "${archivo.nombre}"?`)) return;
    const next = archivosOf(entry).filter(a => a.url !== archivo.url);
    const { data, error } = await supabase.from('manuales').update({ archivos: next }).eq('id', entry.id).select();
    if (error || !data?.[0]) { showToast('Error al eliminar archivo', 'error'); return; }
    await removeFromStorage([archivo]);
    await removeAnnotationsFor(entry.id, [archivo]);
    setEntries(prev => prev.map(e => (e.id === entry.id ? data[0] : e)));
    showToast('Archivo eliminado');
  }

  function toggleExpanded(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleDelete(entry) {
    if (!canEditEntry(entry)) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`¿Eliminar "${entry.titulo}"?`)) return;
    const { error } = await supabase.from('manuales').update({ activo: false }).eq('id', entry.id);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    setEntries(prev => prev.filter(e => e.id !== entry.id));
    showToast('Entrada eliminada');
  }

  async function regenerateRecommendation(entry) {
    if (!canEditEntry(entry)) return;
    const text = await generateRecommendation(entry.descripcion);
    if (!text) { showToast('No se pudo regenerar (revisa OPENAI_API_KEY)', 'error'); return; }
    const { data, error } = await supabase.from('manuales').update({ recomendacion_ia: text }).eq('id', entry.id).select();
    if (error || !data?.[0]) { showToast('Error al guardar recomendación', 'error'); return; }
    setEntries(prev => prev.map(e => e.id === entry.id ? data[0] : e));
    showToast('Recomendación IA actualizada');
  }

  function renderCardFiles(entry) {
    const archivos = archivosOf(entry);
    if (archivos.length === 0) return null;
    const images = archivos.filter(a => a.tipo === 'imagen');
    const pdfs = archivos.filter(a => a.tipo !== 'imagen');
    const extra = images.length - MAX_THUMBS;
    return (
      <div style={s.filesBlock}>
        {images.length > 0 && (
          <div style={{ ...s.thumbGrid, gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)' }}>
            {images.slice(0, MAX_THUMBS).map((img, i) => {
              const showMore = i === MAX_THUMBS - 1 && extra > 0;
              return (
                <div key={img.url} style={s.thumbWrap}>
                  <button
                    type="button"
                    style={s.thumbBtn}
                    onClick={() => setViewer({ entryId: entry.id, index: archivos.indexOf(img) })}
                    title={img.nombre}
                  >
                    <img src={img.url} alt={img.nombre} style={s.thumbImg} loading="lazy" />
                    {showMore && <span style={s.thumbMore}>+{extra} más</span>}
                    {!showMore && annCounts[img.url] > 0 && (
                      <span style={s.annBadge} title={`${annCounts[img.url]} anotaciones`}>✏️ {annCounts[img.url]}</span>
                    )}
                  </button>
                  {!showMore && canDeleteFile(img) && (
                    <button type="button" style={isCompact ? { ...s.fileX, ...s.fileXTouch } : s.fileX} onClick={() => deleteArchivo(entry, img)} title="Eliminar archivo" aria-label="Eliminar archivo">✕</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {pdfs.map(pdf => (
          <div key={pdf.url} style={{ ...s.pdfRow, ...(isMobile ? { flexWrap: 'wrap' } : {}) }}>
            <span style={s.pdfIcon} aria-hidden="true">📄</span>
            <div style={{ ...s.pdfInfo, ...(isMobile ? { flexBasis: 'calc(100% - 32px)' } : {}) }}>
              <span style={s.pdfName} title={pdf.nombre}>{pdf.nombre}</span>
              <span style={s.pdfSize}>{fmtSize(pdf.tamano)}</span>
            </div>
            {annCounts[pdf.url] > 0 && (
              <span style={s.annBadgeInline} title={`${annCounts[pdf.url]} anotaciones`}>✏️ {annCounts[pdf.url]}</span>
            )}
            <button type="button" style={isCompact ? { ...s.linkBtn, ...s.linkBtnTouch } : s.linkBtn} onClick={() => setViewer({ entryId: entry.id, index: archivos.indexOf(pdf) })}>Ver</button>
            <a href={downloadUrl(pdf)} style={isCompact ? { ...s.linkBtn, ...s.linkBtnTouch } : s.linkBtn}>Descargar</a>
            {canDeleteFile(pdf) && (
              <button type="button" style={isCompact ? { ...s.fileXInline, ...tap } : s.fileXInline} onClick={() => deleteArchivo(entry, pdf)} title="Eliminar archivo" aria-label="Eliminar archivo">✕</button>
            )}
          </div>
        ))}
      </div>
    );
  }

  const viewerEntry = viewer ? entries.find(e => e.id === viewer.entryId) : null;

  const activeManualLabel = activeTipo === 'obra' ? 'Manual de Obra' : 'Manual de Diseño';
  const activeTypeDef = ENTRY_TYPES.find(t => t.value === filterEntryType) || null;

  const categoriaSelect = (
    <select
      value={filterCategoria}
      onChange={e => setFilterCategoria(e.target.value)}
      style={{ ...s.filterSelect, ...(isMobile ? { width: '100%', padding: '11px 12px', fontSize: 14 } : {}) }}
    >
      <option value="all">Categoría: todas</option>
      {categoriaOptionsForFilter.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
  );
  const onlyFilesToggle = (
    <button
      type="button"
      onClick={() => setOnlyWithFiles(x => !x)}
      style={{
        ...s.toggleBtn,
        ...(onlyWithFiles ? s.toggleBtnActive : {}),
        ...(isMobile ? { width: '100%', padding: '11px 12px', fontSize: 14, textAlign: 'left' } : {}),
      }}
      aria-pressed={onlyWithFiles}
    >
      📎 Solo con archivos
    </button>
  );

  function renderEmptyState() {
    const tabEmpty = typeCounts[filterEntryType] === 0 && !hasActiveFilters;
    const icon = tabEmpty ? (activeTypeDef?.icon || '📖') : '🔍';
    const message = tabEmpty
      ? `${activeTypeDef ? activeTypeDef.emptyText : 'No hay entradas registradas'} en el ${activeManualLabel}`
      : 'Ninguna entrada coincide con la búsqueda o los filtros';
    return (
      <div style={{ ...s.emptyState, ...(isMobile ? { padding: '36px 16px' } : {}) }}>
        <div style={s.emptyIcon} aria-hidden="true">{icon}</div>
        <p style={s.emptyText}>{message}</p>
        <div style={s.emptyActions}>
          {!tabEmpty && (
            <button type="button" style={s.cancelBtn} onClick={clearFilters}>Limpiar filtros</button>
          )}
          <button type="button" style={s.addBtn} onClick={openCreate}>+ Agregar entrada</button>
        </div>
      </div>
    );
  }

  function renderCard(entry) {
    const t = ENTRY_TYPES.find(x => x.value === entry.tipo_entrada) || ENTRY_TYPES[0];
    const canEdit = canEditEntry(entry);
    const contenido = entry.contenido || '';
    const isLong = contenido.length > CONTENIDO_PREVIEW;
    const isOpen = expanded.has(entry.id);
    const shownContenido = isLong && !isOpen ? truncateText(contenido, CONTENIDO_PREVIEW) : contenido;
    const actionsVisible = !canHover || hoveredCard === entry.id;
    return (
      <article
        key={entry.id}
        style={{ ...s.card, ...(isMobile ? { padding: 16 } : {}) }}
        onMouseEnter={() => setHoveredCard(entry.id)}
        onMouseLeave={() => setHoveredCard(h => (h === entry.id ? null : h))}
        onFocusCapture={() => setHoveredCard(entry.id)}
      >
        {/* 1. badge · categoría · fecha */}
        <div style={s.cardTop}>
          <span style={{ ...s.typeBadge, background: t.bg, color: t.color, borderColor: t.border }}>
            <span aria-hidden="true">{t.icon}</span> {t.label}
          </span>
          {entry.categoria && <span style={s.categoriaPill}>{entry.categoria}</span>}
          <span style={s.cardDate}>{fmtEntryDate(entry.created_at)}</span>
        </div>

        {/* 2. título */}
        <h3 style={s.cardTitle}>{entry.titulo}</h3>

        {/* 3. proyecto + autor */}
        {(entry.proyecto_nombre || entry.autor) && (
          <p style={s.cardMeta}>
            {entry.proyecto_nombre && <>Proyecto: {entry.proyecto_nombre}</>}
            {entry.proyecto_nombre && entry.autor && ' · '}
            {entry.autor && <>Por {entry.autor}</>}
          </p>
        )}

        {/* 4. descripción */}
        <RichText text={entry.descripcion} />

        {/* 5. contenido */}
        {contenido && (
          <div style={s.contenidoBox}>
            <RichText text={shownContenido} />
            {isLong && (
              <button type="button" style={isCompact ? { ...s.verMasBtn, minHeight: 44 } : s.verMasBtn} onClick={() => toggleExpanded(entry.id)}>
                {isOpen ? 'Ver menos' : 'Ver más'}
              </button>
            )}
          </div>
        )}

        {/* 6. recomendación IA */}
        {entry.recomendacion_ia && (
          <div style={s.aiBox}>
            <span style={s.aiLabel}>✨ Recomendación IA</span>
            <RichText text={entry.recomendacion_ia} color="#2A1A1E" fontSize={14.5} lineHeight={1.6} />
          </div>
        )}

        {/* 7. adjuntos */}
        {renderCardFiles(entry)}

        {/* 8. acciones */}
        {canEdit && (
          <div
            style={{
              ...s.cardActions,
              opacity: actionsVisible ? 1 : 0,
              pointerEvents: actionsVisible ? 'auto' : 'none',
            }}
          >
            <button type="button" style={isCompact ? { ...s.smallBtn, ...s.smallBtnTouch } : s.smallBtn} onClick={() => regenerateRecommendation(entry)} title="Regenerar recomendación IA">↻ IA</button>
            <button type="button" style={isCompact ? { ...s.smallBtn, ...s.smallBtnTouch } : s.smallBtn} onClick={() => openEdit(entry)}>Editar</button>
            <button type="button" style={{ ...s.smallBtn, ...(isCompact ? s.smallBtnTouch : {}), color: '#B83B3B', borderColor: 'rgba(184,59,59,0.3)' }} onClick={() => handleDelete(entry)}>Eliminar</button>
          </div>
        )}
      </article>
    );
  }

  return (
    <div style={{ ...s.container, ...(isMobile ? { padding: '20px 16px' } : {}) }}>
      <header style={s.header}>
        <div>
          <h2 style={{ ...s.title, ...(isMobile ? { fontSize: 28 } : {}) }}>Manuales</h2>
          <p style={s.subtitle}>Lecciones aprendidas y recomendaciones del equipo</p>
        </div>
        <button type="button" style={{ ...s.addBtn, ...(isMobile ? { width: '100%', padding: '11px 18px' } : {}) }} onClick={openCreate}>
          + Agregar entrada
        </button>
      </header>

      <div style={{ ...s.tabs, ...(isMobile ? { display: 'flex', width: '100%' } : {}) }}>
        {TAB_DEFS.map(t => (
          <button
            key={t.value}
            type="button"
            onClick={() => { setActiveTipo(t.value); setFilterCategoria('all'); }}
            style={{ ...s.tab, ...(activeTipo === t.value ? s.tabActive : {}), ...(isMobile ? { flex: 1, padding: '9px 8px' } : {}) }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={s.typeTabs} role="tablist" aria-label="Tipo de entrada">
        {[{ value: 'all', plural: 'Todos', icon: null }, ...ENTRY_TYPES].map(t => {
          const active = filterEntryType === t.value;
          return (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setFilterEntryType(t.value)}
              style={{ ...s.typeTab, ...(isCompact ? { minHeight: 44 } : {}), ...(active ? s.typeTabActive : {}) }}
            >
              {t.icon && <span aria-hidden="true">{t.icon}</span>}
              {t.plural}
              <span style={{ ...s.typeTabCount, ...(active ? s.typeTabCountActive : {}) }}>{typeCounts[t.value] ?? 0}</span>
            </button>
          );
        })}
      </div>

      <div style={s.filtersRow}>
        <div style={{ ...s.searchWrap, ...(isMobile ? { maxWidth: 'none', flex: '1 1 0' } : {}) }}>
          <span style={s.searchIcon} aria-hidden="true">🔍</span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar en el manual..."
            style={{ ...s.searchInput, ...(isMobile ? { fontSize: 16 } : {}) }}
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} style={s.searchClearBtn} aria-label="Limpiar">×</button>
          )}
        </div>
        {isMobile ? (
          <button
            type="button"
            style={{ ...s.toggleBtn, ...(sheetFilterCount ? s.toggleBtnActive : {}), flexShrink: 0 }}
            onClick={() => setFiltersSheetOpen(true)}
          >
            Filtros{sheetFilterCount ? ` (${sheetFilterCount})` : ''}
          </button>
        ) : (
          <>
            {categoriaSelect}
            {onlyFilesToggle}
          </>
        )}
      </div>

      {loading ? (
        <div style={s.emptyMsg}>Cargando manual…</div>
      ) : visibleEntries.length === 0 ? (
        renderEmptyState()
      ) : (
        <div style={{ ...s.cardGrid, gridTemplateColumns: isDesktop ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' }}>
          {visibleEntries.map(renderCard)}
        </div>
      )}

      {isMobile && filtersSheetOpen && (
        <div style={s.sheetOverlay} onClick={() => setFiltersSheetOpen(false)}>
          <div style={s.sheet} onClick={e => e.stopPropagation()} role="dialog" aria-label="Filtros">
            <div style={s.sheetHandle} />
            <div style={s.sheetHeader}>
              <h3 style={s.modalTitle}>Filtros</h3>
              <button type="button" style={s.closeBtn} onClick={() => setFiltersSheetOpen(false)} aria-label="Cerrar">✕</button>
            </div>
            <div style={s.sheetBody}>
              <span style={s.fieldLabel}>Categoría</span>
              {categoriaSelect}
              <span style={s.fieldLabel}>Adjuntos</span>
              {onlyFilesToggle}
            </div>
            <div style={s.sheetFooter}>
              <button type="button" style={{ ...s.cancelBtn, flex: 1, padding: '11px 16px' }} onClick={() => { setFilterCategoria('all'); setOnlyWithFiles(false); }}>
                Limpiar
              </button>
              <button type="button" style={{ ...s.submitBtn, flex: 1, padding: '11px 16px' }} onClick={() => setFiltersSheetOpen(false)}>
                Ver {visibleEntries.length} entrada{visibleEntries.length === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div style={{ ...s.overlay, ...(isMobile ? sheet.overlay : {}) }} onClick={closeModal}>
          <div style={isMobile ? { ...s.modal, ...sheet.panel, overflowY: 'hidden' } : s.modal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...s.modalHeader, ...sheet.header, padding: '0 8px 0 16px', paddingTop: 'env(safe-area-inset-top)' } : s.modalHeader}>
              <h3 style={s.modalTitle}>{editing ? 'Editar entrada' : 'Agregar entrada al manual'}</h3>
              <button type="button" style={isMobile ? { ...s.closeBtn, ...sheet.closeBtn } : s.closeBtn} onClick={closeModal} aria-label="Cerrar">✕</button>
            </div>
            <form onSubmit={handleSubmit} style={isMobile ? { ...s.modalForm, flex: 1, padding: '16px 16px 0' } : s.modalForm}>
              <div style={s.fieldRow}>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Manual</span>
                  <select style={s.input} value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value, categoria: '' }))}>
                    {TAB_DEFS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </label>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Tipo de entrada</span>
                  <select style={s.input} value={form.tipo_entrada} onChange={e => setForm(p => ({ ...p, tipo_entrada: e.target.value }))}>
                    {ENTRY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </label>
              </div>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Título</span>
                <input style={s.input} value={form.titulo} onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))} required />
              </label>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Descripción · qué pasó</span>
                <textarea style={{ ...s.textarea, whiteSpace: 'pre-wrap', lineHeight: 1.55 }} value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))} rows={4} required />
              </label>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Contenido · explicación detallada (opcional)</span>
                <textarea
                  ref={contenidoRef}
                  style={{ ...s.textarea, minHeight: 180, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}
                  value={form.contenido}
                  onChange={e => setForm(p => ({ ...p, contenido: e.target.value }))}
                  rows={9}
                  placeholder={'Contexto, causas, cómo se resolvió…\n\nChecklist:\n☐ Primer paso\n☐ Segundo paso'}
                />
              </label>
              <div style={s.formatHelp}>
                <div style={s.formatBtns}>
                  {[
                    { key: 'check', label: '☐ Checklist' },
                    { key: 'bullet', label: '• Viñeta' },
                    { key: 'bold', label: '**Negrita**' },
                  ].map(b => (
                    <button
                      key={b.key}
                      type="button"
                      style={isCompact ? { ...s.formatBtn, minHeight: 44 } : s.formatBtn}
                      // Keep the textarea's selection when clicking.
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => insertFormat(b.key)}
                    >{b.label}</button>
                  ))}
                </div>
                <p style={s.formatHint}>
                  Puedes usar ☐ para checklists, - para viñetas, y **negrita**.
                  Los saltos de línea se respetan.
                </p>
              </div>
              <div style={s.fieldGroup}>
                <span style={s.fieldLabel}>Archivos adjuntos</span>
                <div
                  style={{ ...s.dropZone, ...(dragOver ? s.dropZoneActive : {}) }}
                  onDragOver={e => { e.preventDefault(); if (!saving) setDragOver(true); }}
                  onDragLeave={e => { e.preventDefault(); setDragOver(false); }}
                  onDrop={e => {
                    e.preventDefault();
                    setDragOver(false);
                    if (!saving) addFiles(e.dataTransfer.files);
                  }}
                >
                  <span style={s.dropText}>Arrastra imágenes o PDFs aquí</span>
                  <button type="button" style={s.selectFilesBtn} onClick={() => fileInputRef.current?.click()} disabled={saving}>
                    Seleccionar archivos
                  </button>
                  <span style={s.dropHint}>JPG, PNG, WEBP, GIF o PDF · máx. 10 MB por archivo</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPT_ATTR}
                    style={{ display: 'none' }}
                    onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
                  />
                </div>
                {fileErrors.length > 0 && (
                  <div style={s.fileErrorBox}>
                    {fileErrors.map(msg => <div key={msg}>⚠ {msg}</div>)}
                  </div>
                )}
                {modalFiles.length > 0 && (
                  <div style={s.modalFileList}>
                    {modalFiles.map(item => {
                      const thumb = item.kind === 'existing' ? item.archivo.url : item.previewUrl;
                      const removable = item.kind === 'new' ? !saving : (!saving && canDeleteFile(item.archivo));
                      return (
                        <div key={item.key} style={s.modalFileRow}>
                          {item.tipo === 'imagen' && thumb
                            ? <img src={thumb} alt="" style={s.modalFileThumb} />
                            : <span style={s.modalFileIcon} aria-hidden="true">📄</span>}
                          <div style={s.modalFileInfo}>
                            <span style={s.pdfName} title={item.nombre}>{item.nombre}</span>
                            <span style={s.pdfSize}>
                              {fmtSize(item.tamano)}
                              {item.kind === 'new' && item.status === 'idle' && ' · pendiente de subir'}
                              {item.status === 'error' && <span style={{ color: '#B83B3B' }}> · {item.error}</span>}
                            </span>
                            {item.kind === 'new' && item.status === 'uploading' && (
                              <div style={s.progressTrack}>
                                <div style={{ ...s.progressBar, width: `${item.progress}%` }} />
                              </div>
                            )}
                          </div>
                          {item.kind === 'new' && item.status === 'uploading' && (
                            <span style={s.progressPct}>{item.progress}%</span>
                          )}
                          {removable && (
                            <button type="button" style={isCompact ? { ...s.fileXInline, ...tap } : s.fileXInline} onClick={() => removeModalFile(item)} title="Quitar archivo" aria-label="Quitar archivo">✕</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div style={s.fieldRow}>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Proyecto asociado</span>
                  <select style={s.input} value={form.proyecto_nombre} onChange={e => setForm(p => ({ ...p, proyecto_nombre: e.target.value }))}>
                    <option value="">—</option>
                    {projects.map(p => <option key={p.id} value={p.nombre}>{p.nombre}</option>)}
                  </select>
                </label>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Categoría</span>
                  <select style={s.input} value={form.categoria} onChange={e => setForm(p => ({ ...p, categoria: e.target.value }))}>
                    <option value="">—</option>
                    {categoriasForTipo.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                  </select>
                </label>
              </div>
              <div style={isMobile ? { ...s.modalActions, ...sheet.footer, margin: '4px -16px 0', padding: '12px 16px', paddingBottom: 'max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid #f0ebe3', background: '#FDFBF8' } : s.modalActions}>
                <button type="button" style={s.cancelBtn} onClick={closeModal} disabled={saving}>Cancelar</button>
                <button type="submit" style={s.submitBtn} disabled={saving}>
                  {saving ? 'Guardando…' : (editing ? 'Guardar cambios' : 'Crear entrada')}
                </button>
              </div>
              <p style={s.aiHint}>✨ Se generará automáticamente una recomendación práctica con IA al guardar.</p>
            </form>
          </div>
        </div>
      )}

      {viewerEntry && archivosOf(viewerEntry).length > 0 && (
        <Suspense fallback={<div style={s.viewerLoading}>Abriendo visor…</div>}>
        <AttachmentViewer
          key={viewerEntry.id}
          manualId={viewerEntry.id}
          archivos={archivosOf(viewerEntry)}
          startIndex={Math.min(viewer.index, archivosOf(viewerEntry).length - 1)}
          onClose={() => setViewer(null)}
          isAdmin={isAdmin}
          myEmail={myEmail}
          myAutor={myAutor}
          team={team}
          onCountChange={(url, delta) => setAnnCounts(prev => ({ ...prev, [url]: Math.max(0, (prev[url] || 0) + delta) }))}
        />
        </Suspense>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          background: toast.type === 'error' ? '#B83B3B' : '#4A7C59',
          color: '#fff', padding: '10px 24px', borderRadius: 10, fontSize: 13, fontWeight: 600,
          zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          ...(isMobile ? toastMobile : null),
        }}>{toast.message}</div>
      )}
    </div>
  );
}

const WINE = '#6B1C2A';

const s = {
  container: { padding: '32px 36px', maxWidth: 1400 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, gap: 16, flexWrap: 'wrap' },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 600, color: WINE, margin: 0 },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 },
  addBtn: {
    padding: '9px 18px', borderRadius: 8, border: 'none',
    background: WINE, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    fontFamily: "'DM Sans',sans-serif",
  },

  tabs: {
    display: 'inline-flex', gap: 2, padding: 3,
    background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
    marginBottom: 14,
  },
  tab: {
    padding: '8px 16px', border: 'none', background: 'transparent',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    color: 'var(--text-secondary)', borderRadius: 7,
    fontFamily: "'DM Sans',sans-serif",
  },
  tabActive: { background: WINE, color: '#fff' },

  filtersRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 },
  searchWrap: {
    position: 'relative', display: 'flex', alignItems: 'center',
    background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
    padding: '0 12px', flex: '1 1 280px', maxWidth: 420,
  },
  searchIcon: { fontSize: 13, opacity: 0.55, marginRight: 8 },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    padding: '10px 0', fontSize: 13, fontFamily: "'DM Sans',sans-serif",
  },
  searchClearBtn: {
    border: 'none', background: 'transparent', fontSize: 18, lineHeight: 1,
    color: 'var(--text-light)', cursor: 'pointer', padding: '0 4px',
  },
  filterSelect: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
    background: '#fff', fontSize: 12, fontFamily: "'DM Sans',sans-serif",
    color: 'var(--text-primary)', outline: 'none',
  },

  emptyMsg: {
    background: '#fff', borderRadius: 12, border: '1px solid var(--border)',
    padding: '40px 24px', textAlign: 'center', color: 'var(--text-light)', fontSize: 14,
  },

  emptyState: {
    background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(74,18,32,0.06)',
    padding: '56px 24px', textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
  },
  emptyIcon: { fontSize: 40, lineHeight: 1 },
  emptyText: {
    fontFamily: "'Cormorant Garamond', serif", fontSize: 21, fontWeight: 600,
    color: WINE, margin: 0, lineHeight: 1.35, maxWidth: '40ch',
  },
  emptyActions: { display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginTop: 6 },

  typeTabs: {
    display: 'flex', gap: 8, marginBottom: 14,
    overflowX: 'auto', WebkitOverflowScrolling: 'touch',
    paddingBottom: 4, scrollbarWidth: 'thin',
  },
  typeTab: {
    display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, whiteSpace: 'nowrap',
    padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
    background: '#F7F3EE', border: `1px solid ${WINE}`, color: WINE,
    fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif",
  },
  typeTabActive: { background: WINE, color: '#fff' },
  typeTabCount: {
    minWidth: 20, padding: '1px 7px', borderRadius: 999, fontSize: 11.5, fontWeight: 700,
    background: 'rgba(107,28,42,0.1)', color: WINE, textAlign: 'center',
  },
  typeTabCountActive: { background: 'rgba(255,255,255,0.22)', color: '#fff' },

  cardGrid: { display: 'grid', gap: 20, alignItems: 'start' },
  card: {
    background: '#fff', borderRadius: 14, padding: 24,
    boxShadow: '0 1px 3px rgba(74,18,32,0.06), 0 4px 16px rgba(74,18,32,0.06)',
    display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0,
  },
  cardTop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  typeBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 999,
    border: '1px solid transparent', lineHeight: 1,
  },
  categoriaPill: {
    fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
    background: '#F0EBE3', padding: '6px 12px', borderRadius: 999, lineHeight: 1,
  },
  cardDate: { marginLeft: 'auto', fontSize: 12, color: '#8A8580', whiteSpace: 'nowrap' },
  cardTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 20, fontWeight: 600, color: WINE, margin: 0, lineHeight: 1.3,
    overflowWrap: 'anywhere',
  },
  cardMeta: { fontSize: 12.5, color: '#8A8580', margin: '-6px 0 0', lineHeight: 1.4 },
  cardDesc: {
    fontFamily: "'DM Sans',sans-serif", fontSize: 15, color: '#3A3A3A',
    lineHeight: 1.7, margin: 0, maxWidth: '70ch', overflowWrap: 'anywhere',
  },
  aiBox: {
    display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 14px',
    background: '#FBF6F7', borderLeft: `3px solid ${WINE}`, borderRadius: '0 8px 8px 0',
  },
  aiLabel: { fontSize: 12, fontWeight: 700, fontStyle: 'italic', color: WINE, letterSpacing: '0.01em' },
  aiText: { fontSize: 14.5, color: '#2A1A1E', lineHeight: 1.6, margin: 0, maxWidth: '70ch' },
  cardActions: {
    display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end',
    transition: 'opacity 0.15s',
  },
  smallBtn: {
    padding: '5px 10px', borderRadius: 6,
    border: '1px solid var(--border)', background: '#fff',
    color: 'var(--text-secondary)', fontSize: 11.5, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'DM Sans',sans-serif",
  },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(20,16,18,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10000, padding: 24,
  },
  modal: {
    background: '#FDFBF8', borderRadius: 14,
    width: 'min(620px, 100%)', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
    border: '1px solid rgba(107,28,42,0.15)',
    fontFamily: "'DM Sans',sans-serif",
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 22px', borderBottom: '1px solid #f0ebe3',
  },
  modalTitle: { fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 600, color: WINE, margin: 0 },
  closeBtn: { border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--text-secondary)' },
  modalForm: { display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 22px 20px', overflowY: 'auto' },
  fieldRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 5, flex: '1 1 200px' },
  fieldLabel: { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: {
    padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)',
    background: '#fff', fontSize: 13, fontFamily: "'DM Sans',sans-serif", outline: 'none',
  },
  textarea: {
    padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)',
    background: '#fff', fontSize: 13, fontFamily: "'DM Sans',sans-serif",
    outline: 'none', resize: 'vertical',
  },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 },
  cancelBtn: {
    padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)',
    background: '#fff', color: 'var(--text-primary)', fontSize: 12.5, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'DM Sans',sans-serif",
  },
  submitBtn: {
    padding: '8px 18px', borderRadius: 8, border: 'none',
    background: WINE, color: '#fff', fontSize: 12.5, fontWeight: 700,
    cursor: 'pointer', fontFamily: "'DM Sans',sans-serif",
  },
  aiHint: { margin: 0, fontSize: 11.5, color: 'var(--text-light)', textAlign: 'right' },

  toggleBtn: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
    background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    color: 'var(--text-secondary)', fontFamily: "'DM Sans',sans-serif",
  },
  toggleBtnActive: { background: WINE, borderColor: WINE, color: '#fff' },

  contenidoBox: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
  contenidoPara: {
    fontFamily: "'DM Sans',sans-serif", fontSize: 15, color: '#3A3A3A',
    lineHeight: 1.7, margin: '0 0 12px', maxWidth: '70ch',
    whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
  },
  verMasBtn: {
    border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', marginTop: -4,
    color: WINE, fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
  },

  formatHelp: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: -4 },
  formatBtns: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  formatBtn: {
    padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(107,28,42,0.25)',
    background: '#fff', color: WINE, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'DM Sans',sans-serif",
  },
  formatHint: { margin: 0, fontSize: 11.5, color: 'var(--text-light)', lineHeight: 1.5 },

  sheetOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(20,16,18,0.45)', zIndex: 10000,
    display: 'flex', alignItems: 'flex-end',
  },
  sheet: {
    width: '100%', background: '#FDFBF8', borderRadius: '16px 16px 0 0',
    boxShadow: '0 -10px 30px rgba(0,0,0,0.2)', fontFamily: "'DM Sans',sans-serif",
    paddingBottom: 'env(safe-area-inset-bottom)', maxHeight: '80vh', overflowY: 'auto',
  },
  sheetHandle: { width: 40, height: 4, borderRadius: 2, background: '#D8CFC4', margin: '10px auto 0' },
  sheetHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px 6px' },
  sheetBody: { display: 'flex', flexDirection: 'column', gap: 8, padding: '6px 16px 16px' },
  sheetFooter: { display: 'flex', gap: 8, padding: '12px 16px 16px', borderTop: '1px solid #f0ebe3' },

  filesBlock: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 2 },
  thumbGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 },
  thumbWrap: { position: 'relative', aspectRatio: '1 / 1' },
  thumbBtn: {
    position: 'relative', width: '100%', height: '100%', padding: 0,
    border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
    cursor: 'zoom-in', background: '#F7F3EE', display: 'block',
  },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  thumbMore: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(74,18,32,0.62)', color: '#fff', fontSize: 13, fontWeight: 700,
  },
  fileX: {
    position: 'absolute', top: 3, right: 3, width: 18, height: 18, borderRadius: '50%',
    border: 'none', background: 'rgba(20,16,18,0.65)', color: '#fff',
    fontSize: 9, lineHeight: '18px', padding: 0, cursor: 'pointer',
  },
  fileXInline: {
    border: 'none', background: 'transparent', color: 'var(--text-light)',
    fontSize: 12, cursor: 'pointer', padding: '2px 4px', flexShrink: 0,
  },
  pdfRow: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
    border: '1px solid var(--border)', borderRadius: 8, background: '#FDFBF8',
  },
  pdfIcon: { fontSize: 16, flexShrink: 0 },
  pdfInfo: { display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 },
  pdfName: {
    fontSize: 12, fontWeight: 600, color: 'var(--text-primary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  pdfSize: { fontSize: 10.5, color: 'var(--text-light)' },
  linkBtn: {
    padding: '4px 9px', borderRadius: 6, border: '1px solid rgba(107,28,42,0.25)',
    background: '#fff', color: WINE, fontSize: 11, fontWeight: 700, cursor: 'pointer',
    textDecoration: 'none', flexShrink: 0, fontFamily: "'DM Sans',sans-serif",
  },

  dropZone: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    padding: '18px 14px', borderRadius: 10, border: '1.5px dashed rgba(107,28,42,0.3)',
    background: '#fff', textAlign: 'center', transition: 'background 0.15s, border-color 0.15s',
  },
  dropZoneActive: { background: 'rgba(107,28,42,0.06)', borderColor: WINE },
  dropText: { fontSize: 13, color: 'var(--text-secondary)' },
  dropHint: { fontSize: 11, color: 'var(--text-light)' },
  selectFilesBtn: {
    padding: '7px 14px', borderRadius: 8, border: `1px solid ${WINE}`,
    background: '#fff', color: WINE, fontSize: 12, fontWeight: 700,
    cursor: 'pointer', fontFamily: "'DM Sans',sans-serif",
  },
  fileErrorBox: {
    fontSize: 12, color: '#B83B3B', background: 'rgba(184,59,59,0.08)',
    border: '1px solid rgba(184,59,59,0.25)', borderRadius: 8, padding: '8px 10px',
    display: 'flex', flexDirection: 'column', gap: 3,
  },
  modalFileList: { display: 'flex', flexDirection: 'column', gap: 6 },
  modalFileRow: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px',
    border: '1px solid var(--border)', borderRadius: 8, background: '#fff',
  },
  modalFileThumb: { width: 36, height: 36, borderRadius: 6, objectFit: 'cover', flexShrink: 0 },
  modalFileIcon: { width: 36, textAlign: 'center', fontSize: 20, flexShrink: 0 },
  modalFileInfo: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 },
  progressTrack: { height: 4, borderRadius: 2, background: '#F0EBE3', overflow: 'hidden', marginTop: 2 },
  progressBar: { height: '100%', background: WINE, transition: 'width 0.15s' },
  progressPct: { fontSize: 11, fontWeight: 700, color: WINE, flexShrink: 0 },

  viewerLoading: {
    position: 'fixed', inset: 0, zIndex: 10002, background: '#F7F3EE',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: WINE,
  },
  // Touch-only tap-target overrides.
  fileXTouch: { width: 32, height: 32, lineHeight: '32px', fontSize: 12, top: 4, right: 4 },
  linkBtnTouch: { minHeight: 44, padding: '0 14px', display: 'inline-flex', alignItems: 'center' },
  smallBtnTouch: { minHeight: 44, padding: '0 14px', fontSize: 13 },
  annBadge: {
    position: 'absolute', left: 4, bottom: 4, padding: '2px 6px', borderRadius: 10,
    background: 'rgba(107,28,42,0.88)', color: '#fff', fontSize: 10, fontWeight: 700,
    lineHeight: 1.3, pointerEvents: 'none',
  },
  annBadgeInline: {
    padding: '2px 7px', borderRadius: 10, background: 'rgba(107,28,42,0.1)',
    color: WINE, fontSize: 10.5, fontWeight: 700, flexShrink: 0,
  },
};
